import React, { useState, useEffect } from 'react';
import {
  Card,
  Title,
  Text,
  Button,
  TextInput,
  Switch,
  Flex,
  Badge,
  Callout
} from "@tremor/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import * as deviceAPI from "../../services/device";

export default function WebhookForm({ webhook, onSubmit, onCancel }) {
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    secret: '',
    enabled: true,
    eventTypes: ['sensor_data'],
    filters: {
      deviceIds: [],
      deviceSerials: [],
      alertTypes: []
    },
    headers: {},
    retryConfig: {
      maxRetries: 3,
      retryDelay: 1000,
      timeout: 30000
    }
  });
  
  const [devices, setDevices] = useState([]);
  const [customHeaders, setCustomHeaders] = useState([{ key: '', value: '' }]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Load devices for filtering options
    const loadDevices = async () => {
      try {
        const deviceList = await deviceAPI.fetchDevices();
        setDevices(deviceList);
      } catch (err) {
        console.error('Error loading devices:', err);
      }
    };
    loadDevices();

    // Populate form if editing
    if (webhook) {
      setFormData({
        ...formData,
        ...webhook,
        filters: webhook.filters || formData.filters,
        retryConfig: webhook.retryConfig || formData.retryConfig
      });
      
      // Convert headers Map to array for editing
      if (webhook.headers) {
        const headerArray = Object.entries(webhook.headers).map(([key, value]) => ({ key, value }));
        setCustomHeaders(headerArray.length > 0 ? headerArray : [{ key: '', value: '' }]);
      }
    }
  }, [webhook]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.url.trim()) {
      newErrors.url = 'URL is required';
    } else if (!/^https?:\/\/.+/.test(formData.url)) {
      newErrors.url = 'URL must be a valid HTTP or HTTPS URL';
    }
    
    if (formData.eventTypes.length === 0) {
      newErrors.eventTypes = 'At least one event type must be selected';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Convert custom headers array to object
    const headersObject = {};
    customHeaders.forEach(({ key, value }) => {
      if (key.trim() && value.trim()) {
        headersObject[key.trim()] = value.trim();
      }
    });

    const submitData = {
      ...formData,
      headers: headersObject
    };

    onSubmit(submitData);
  };

  const handleEventTypeToggle = (eventType) => {
    const currentTypes = formData.eventTypes;
    const newTypes = currentTypes.includes(eventType)
      ? currentTypes.filter(type => type !== eventType)
      : [...currentTypes, eventType];
    
    setFormData({ ...formData, eventTypes: newTypes });
  };

  const handleDeviceFilter = (deviceId) => {
    const currentIds = formData.filters.deviceIds;
    const newIds = currentIds.includes(deviceId)
      ? currentIds.filter(id => id !== deviceId)
      : [...currentIds, deviceId];
    
    setFormData({
      ...formData,
      filters: { ...formData.filters, deviceIds: newIds }
    });
  };

  const addCustomHeader = () => {
    setCustomHeaders([...customHeaders, { key: '', value: '' }]);
  };

  const removeCustomHeader = (index) => {
    setCustomHeaders(customHeaders.filter((_, i) => i !== index));
  };

  const updateCustomHeader = (index, field, value) => {
    const newHeaders = [...customHeaders];
    newHeaders[index][field] = value;
    setCustomHeaders(newHeaders);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <Flex className="justify-between items-start mb-6">
            <div>
              <Title>{webhook ? 'Edit Integration' : 'Add New Integration'}</Title>
              <Text>Configure a webhook to receive real-time data and alerts.</Text>
            </div>
            <Button 
              variant="light" 
              size="xs" 
              icon={XMarkIcon}
              onClick={onCancel}
            >
            </Button>
          </Flex>

          {/* Basic Settings */}
          <div className="space-y-4 mb-6">
            <div>
              <Text className="font-medium mb-2">Basic Settings</Text>
              <div className="space-y-3">
                <div>
                  <Text className="text-sm mb-1">Name *</Text>
                  <TextInput
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="My Webhook Integration"
                    error={errors.name}
                  />
                  {errors.name && <Text className="text-red-500 text-xs mt-1">{errors.name}</Text>}
                </div>
                
                <div>
                  <Text className="text-sm mb-1">Webhook URL *</Text>
                  <TextInput
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    placeholder="https://your-endpoint.com/webhook"
                    error={errors.url}
                  />
                  {errors.url && <Text className="text-red-500 text-xs mt-1">{errors.url}</Text>}
                </div>
                
                <div>
                  <Text className="text-sm mb-1">Secret Key (Optional)</Text>
                  <TextInput
                    value={formData.secret}
                    onChange={(e) => setFormData({ ...formData, secret: e.target.value })}
                    placeholder="secret-key-for-hmac-verification"
                    type="password"
                  />
                  <Text className="text-xs text-tremor-content-subtle mt-1">
                    Used for HMAC signature verification (sent as X-Zafron-Signature header)
                  </Text>
                </div>
                
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.enabled}
                    onChange={(checked) => setFormData({ ...formData, enabled: checked })}
                  />
                  <Text className="text-sm">Enabled</Text>
                </div>
              </div>
            </div>
          </div>

          {/* Event Types */}
          <div className="space-y-4 mb-6">
            <div>
              <Text className="font-medium mb-2">Event Types *</Text>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.eventTypes.includes('sensor_data')}
                    onChange={() => handleEventTypeToggle('sensor_data')}
                  />
                  <div>
                    <Text className="text-sm">Sensor Data</Text>
                    <Text className="text-xs text-tremor-content-subtle">
                      Receive notifications when devices send measurement data
                    </Text>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.eventTypes.includes('alert')}
                    onChange={() => handleEventTypeToggle('alert')}
                  />
                  <div>
                    <Text className="text-sm">Alerts</Text>
                    <Text className="text-xs text-tremor-content-subtle">
                      Receive notifications when alerts are triggered
                    </Text>
                  </div>
                </div>
              </div>
              {errors.eventTypes && <Text className="text-red-500 text-xs mt-1">{errors.eventTypes}</Text>}
            </div>
          </div>

          {/* Device Filters */}
          <div className="space-y-4 mb-6">
            <div>
              <Text className="font-medium mb-2">Device Filters (Optional)</Text>
              <Text className="text-xs text-tremor-content-subtle mb-3">
                If no devices are selected, all devices will trigger webhooks
              </Text>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {devices.map(device => (
                  <div key={device._id} className="flex items-center gap-2">
                    <Switch
                      checked={formData.filters.deviceIds.includes(device._id)}
                      onChange={() => handleDeviceFilter(device._id)}
                    />
                    <div className="flex-1">
                      <Text className="text-sm">{device.name}</Text>
                      <Text className="text-xs text-tremor-content-subtle">{device.serial}</Text>
                    </div>
                    <Badge size="xs" color={device.type === 'mqtt' ? 'blue' : 'purple'}>
                      {device.type === 'mqtt' ? 'MQTT' : 'LoRaWAN'}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Custom Headers */}
          <div className="space-y-4 mb-6">
            <div>
              <Text className="font-medium mb-2">Custom Headers (Optional)</Text>
              <div className="space-y-2">
                {customHeaders.map((header, index) => (
                  <div key={index} className="flex gap-2">
                    <TextInput
                      value={header.key}
                      onChange={(e) => updateCustomHeader(index, 'key', e.target.value)}
                      placeholder="Header Name"
                      className="flex-1"
                    />
                    <TextInput
                      value={header.value}
                      onChange={(e) => updateCustomHeader(index, 'value', e.target.value)}
                      placeholder="Header Value"
                      className="flex-1"
                    />
                    {customHeaders.length > 1 && (
                      <Button
                        variant="light"
                        size="xs"
                        onClick={() => removeCustomHeader(index)}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="light" size="xs" onClick={addCustomHeader}>
                  Add Header
                </Button>
              </div>
            </div>
          </div>

          {/* Webhook Format Info */}
          <div className="mb-6">
            <Callout title="Webhook Format" color="blue">
              <Text className="text-xs">
                Webhooks are sent as POST requests with JSON payload containing: version, timestamp, eventType, data, and metadata fields.
              </Text>
            </Callout>
          </div>

          {/* Actions */}
          <Flex className="justify-end gap-2">
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit">
              {webhook ? 'Update Integration' : 'Create Integration'}
            </Button>
          </Flex>
        </form>
      </Card>
    </div>
  );
}